/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './AuthContext';
import { useLoanContext } from './LoanContext';

type viewingType = {
  isServicerView: boolean;
  toggleBorrowerView: () => void;
};

const ViewingContext = createContext<viewingType | null>(null);

const useViewingContext = () => {
  const viewingContext = useContext(ViewingContext);
  if (!viewingContext) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('viewingContext must be inside a ViewingProvider');
  }
  return viewingContext;
};

type viewingProviderProps = {
  children: ReactNode;
};

const ViewingProvider = ({ children }: viewingProviderProps): JSX.Element => {
  const navigate = useNavigate();
  const { servicerUser } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const [isBorrowerView, setIsBorrowerView] = useState(false);

  const isServicerView = useMemo(() => servicerUser && !isBorrowerView, [servicerUser, isBorrowerView]);

  const toggleBorrowerView = useCallback(() => {
    if (servicerUser && loanNumber) {
      setIsBorrowerView((prev) => !prev);
      navigate('/loan-information');
    }
  }, [servicerUser, loanNumber, navigate]);

  return <ViewingContext.Provider value={{ isServicerView, toggleBorrowerView }}>{children}</ViewingContext.Provider>;
};

export { ViewingProvider, useViewingContext };
